import moment from 'moment';
import showToaster from '../../helpers/toasterMessages/showToaster';
import { sendPayment } from '../../core/payment/sendPayment';
import { convert } from '../../helpers/currencyConvertion';
import { processStripePayment } from '../../core/payment/stripe/processStripePayment';
import { sendPawaPayDeposit } from '../../core/payment/pawapay/sendDeposit';
import { startTimeData } from '../../helpers/formatting';
import { createReservation as mutation } from '../../lib/graphql';
import { refreshBookingListing } from './bookingProcess';
import {
  BOOKING_PAYMENT_START,
  BOOKING_PAYMENT_SUCCESS,
  BOOKING_PAYMENT_ERROR,
} from '../../constants';

export const makePayment = (
  listId,
  title,
  hostId,
  guestId,
  checkIn,
  checkOut,
  guests,
  message,
  basePrice,
  options,
  currency,
  discount,
  discountType,
  guestServiceFee,
  hostServiceFee,
  total,
  bookingType,
  paymentCurrency,
  paymentType,
  guestEmail,
  specialPricing,
  isSpecialPriceAssigned,
  isSpecialPriceAverage,
  dayDifference,
  startTime,
  endTime,
  licenseNumber,
  firstName,
  middleName,
  lastName,
  dateOfBirth,
  countryCode,
  paymentMethodId,
  securityDeposit,
  hostServiceFeeType,
  hostServiceFeeValue,
  guestServiceFeeType,
  guestServiceFeeValue,
  threadId,
  promoId,
  promoDiscount,
  pawapayCountry,
  pawapayCurrency,
  pawapayPhoneNumber,
  pawapayProvider
) => {

  return async (dispatch, getState, { client }) => {
    try {

      dispatch({
        type: BOOKING_PAYMENT_START,
        payload: {
          paymentLoading: true
        }
      });

      let preApprove, bookingTypeData, cancellationPolicy, isStartValue, isStartDate, checkInDate, checkOutDate, isEndDate, isEndValue;
      let securityDepositStatus, securityDepositAmount;
      preApprove = getState().book?.bookDetails?.preApprove;
      bookingTypeData = preApprove ? 'instant' : bookingType;
      cancellationPolicy = getState().book?.data?.listingData?.cancellation?.id;
      isStartValue = startTimeData(startTime), isEndValue = startTimeData(endTime);
      isStartDate = moment(checkIn).format('YYYY-MM-DD'), isEndDate = moment(checkOut).format('YYYY-MM-DD');
      checkInDate = moment.utc(isStartDate + ' ' + isStartValue), checkOutDate = moment.utc(isEndDate + ' ' + isEndValue);
      securityDepositStatus = getState().siteSettings?.data?.securityDepositPreference;
      securityDepositAmount = securityDepositStatus == 1 ? securityDeposit : 0


      const { data } = await client.mutate({
        mutation,
        variables: {
          listId,
          hostId,
          guestId,
          checkIn: checkInDate,
          checkOut: checkOutDate,
          guests,
          message,
          basePrice,
          // Ids and delivery location only: the server prices the options (D21).
          options: Array.isArray(options) ? options : [],
          currency,
          discount,
          discountType,
          guestServiceFee,
          hostServiceFee,
          total,
          bookingType: bookingTypeData,
          paymentType: Number(paymentType),
          cancellationPolicy,
          specialPricing,
          isSpecialPriceAssigned,
          isSpecialPriceAverage: parseFloat(isSpecialPriceAverage),
          dayDifference,
          startTime,
          endTime,
          licenseNumber,
          firstName,
          middleName,
          lastName,
          dateOfBirth,
          countryCode,
          securityDeposit: securityDepositAmount,
          hostServiceFeeType,
          hostServiceFeeValue,
          guestServiceFeeType,
          guestServiceFeeValue,
          threadId,
          promoId,
          promoDiscount
        }
      })

      const createdReservation = data?.createReservation;
      if (createdReservation && !createdReservation.id) {
        // The server refused the booking (amount mismatch, unavailable option, invalid location...).
        const requestMessage = createdReservation.errorMessage;
        if (requestMessage) showToaster({ messageId: 'commonError', toasterType: 'error', requestMessage });
        // Options or prices may have changed: reload them so the page shows what the server will accept.
        await dispatch(refreshBookingListing(listId));
        dispatch({
          type: BOOKING_PAYMENT_ERROR,
          payload: { paymentLoading: false }
        });
        // Not 400: that status means "card action required" for the Stripe flow.
        return { status: 'bookingFailed', errorMessage: requestMessage };
      }

      if (createdReservation) {
        let reservationId, amount, rates, baseCurrency, convertedAmount = 0, reservationDetails;
        reservationId = createdReservation.id;
        // Amount priced by the server (the payment routes recompute it from the stored reservation).
        amount = Number(createdReservation.total) + Number(createdReservation.guestServiceFee) + Number(createdReservation.securityDeposit || 0);
        currency = createdReservation.currency || currency;

        rates = getState().currency?.rates;
        baseCurrency = getState().currency?.base;

        if (paymentType == 1) {
          convertedAmount = convert(baseCurrency, rates, amount, currency, paymentCurrency);
          const { status, errorMessage } = await sendPayment(reservationId, convertedAmount.toFixed(2), paymentCurrency, title);
          if (status == 200) {
            dispatch({
              type: BOOKING_PAYMENT_SUCCESS,
              payload: { paymentLoading: false }
            });
          } else {
            if (status == 422) {
              showToaster({ messageId: 'invalidCurrency', toasterType: 'error' })
            }
            else {
              errorMessage ? showToaster({ messageId: 'checkStatus', toasterType: 'error', requestMessage: errorMessage }) : '';
            }
            dispatch({
              type: BOOKING_PAYMENT_ERROR,
              payload: { paymentLoading: false }
            });
          }

        } else if (paymentType == 3) {
          const { status, errorMessage } = await sendPawaPayDeposit(reservationId, pawapayCountry, pawapayCurrency, pawapayPhoneNumber, pawapayProvider);
          if (status === 200) {
            dispatch({ type: BOOKING_PAYMENT_SUCCESS, payload: { paymentLoading: false } });
            return { status, reservationId };
          }
          dispatch({ type: BOOKING_PAYMENT_ERROR, payload: { paymentLoading: false } });
          return { status, reservationId, errorMessage };
        } else {
          reservationDetails = {
            reservationId,
            listId,
            hostId,
            guestId,
            guestEmail,
            title,
            amount,
            currency
          };

          const { status, errorMessage, paymentIntentSecret } = await processStripePayment(
            'reservation',
            null,
            reservationDetails,
            paymentMethodId
          );

          if (status === 200) {
            dispatch({
              type: BOOKING_PAYMENT_SUCCESS,
              payload: { paymentLoading: true }
            });

            return {
              status
            }
          } else {
            errorMessage ? showToaster({ messageId: 'failedError', toasterType: 'error', requestMessage: errorMessage }) : '';
            dispatch({
              type: BOOKING_PAYMENT_ERROR,
              payload: { paymentLoading: false }
            });

            return {
              status,
              paymentIntentSecret,
              reservationId
            }
          }
        }
      }
    } catch (error) {
      dispatch({
        type: BOOKING_PAYMENT_ERROR,
        payload: {
          error,
          paymentLoading: false
        }
      });
      return false;
    }

    return true;
  };
}
